import React, { FC, useCallback } from 'react';
import {
    IFilterCategory,
    IFilterRule,
    IPriceRange,
    ISelectFilter,
} from '../../../../redux/filters/filter.duck.interface';
import MultiSelect from '@components/MultiSelect';
import useTranslations from '@hooks/useTranslations';
import ColorFilterItem from './ColorFilterItem';
import { useGTM } from '@hooks/useGTM';
import { GTMActionValues, capitalizeFirstLetter } from '../FilterBoxTemplate';
import { FILTER_CATEGORY } from '../../../../constants/main';

interface GeneratedExtraFieldsProps {
    extraFilters: IFilterRule[];
    availableFilters: IFilterRule[];
    filterCategories: IFilterCategory[];
    handleChange: (name: string, value: boolean | IPriceRange) => void;
}

export type ICategoryWithFilters = { [key: string]: Array<IFilterRule | ISelectFilter> };

const GeneratedExtraFilters: FC<GeneratedExtraFieldsProps> = ({
    filterCategories,
    availableFilters,
    extraFilters,
    handleChange,
}) => {
    const { t } = useTranslations();
    const { pushUserAction } = useGTM();
    const mappedExtraFilterByCategories: ICategoryWithFilters = extraFilters.reduce((acc, filter) => {
        const isEnabled = availableFilters.find((availableFilter) => availableFilter.name === filter.name);

        if (!acc[filter.parent]?.length) {
            acc[filter.parent] = [];
        }

        if (filter.parent !== (FILTER_CATEGORY.COLOR_GROUPS as string)) {
            const mappedFilter: ISelectFilter = {
                label: filter.label,
                value: filter.value as boolean,
                disabled: !isEnabled,
                name: filter.name,
                parent: filter.parent,
            };

            return {
                ...acc,
                [filter.parent]: [...acc[filter.parent], mappedFilter],
            };
        }

        return {
            ...acc,
            [filter.parent]: [...acc[filter.parent], { ...filter, disabled: !isEnabled }],
        };
    }, {} as ICategoryWithFilters);

    const handleColorClick = useCallback(
        (name: string, value: boolean | IPriceRange, hexCode: string) => () => {
            pushUserAction({
                action: `${GTMActionValues.FILTER}::${capitalizeFirstLetter(FILTER_CATEGORY.COLOR)}`,
                label: hexCode,
            });

            handleChange(name, !value);
        },
        [handleChange]
    );

    const handleSelectChange = useCallback(
        (value: ISelectFilter) => {
            pushUserAction({
                action:
                    (value as { parent: string }).parent === FILTER_CATEGORY.GRADES
                        ? `${GTMActionValues.FILTER}::${capitalizeFirstLetter(FILTER_CATEGORY.FINITION)}`
                        : `${GTMActionValues.FILTER}::${capitalizeFirstLetter(FILTER_CATEGORY.MOTORIZATION)}`,
                label: value.label,
            });

            handleChange(value.name, !value.value);
        },
        [handleChange]
    );

    const getCategoryDisplayNameTranslated = useCallback((displayName: string): string => {
        switch (displayName) {
            case FILTER_CATEGORY.GRADES:
                return t('filters.category.grades');
            case FILTER_CATEGORY.ENGINES:
                return t('filters.category.engines');
            case FILTER_CATEGORY.COLOR_GROUPS:
                return t('filters.category.colorGroups');
            default:
                return displayName;
        }
    }, []);

    const RenderColors = () => {
        const category = filterCategories.find(({ name }) => name === FILTER_CATEGORY.COLOR_GROUPS);

        if (!category) {
            console.error(`No category found for key ${FILTER_CATEGORY.COLOR_GROUPS}!`);
            return <></>;
        }

        return (
            <div className="filter color-filter">
                <h3 className="filter-title">{getCategoryDisplayNameTranslated(category.name)}</h3>
                {
                    <div className="color-filter__content">
                        {mappedExtraFilterByCategories[FILTER_CATEGORY.COLOR_GROUPS]?.map((filter: IFilterRule) => {
                            return (
                                <ColorFilterItem
                                    key={filter.name}
                                    isActive={filter.value as boolean}
                                    color={filter.hexCode}
                                    ariaLabel={`${t('colorSwitcher.ariaLabel')} ${filter.displayName}`}
                                    className="color-filter-item"
                                    handleClick={handleColorClick(filter.name, filter.value, filter.hexCode)}
                                />
                            );
                        })}
                    </div>
                }
            </div>
        );
    };

    const RenderMultiSelect = () => {
        return (
            <div className="MultiSelect-container">
                {Object.keys(mappedExtraFilterByCategories).map((key) => {
                    const category = filterCategories.find((category) => category.name === key);

                    if (!category) {
                        console.error(`No category found for key ${key}!`);
                        return;
                    }

                    if (key !== FILTER_CATEGORY.COLOR_GROUPS) {
                        return (
                            <div key={category.name} className="filter">
                                <h3 className="filter-title">{getCategoryDisplayNameTranslated(category.name)}</h3>

                                {key !== FILTER_CATEGORY.COLOR_GROUPS && (
                                    <MultiSelect
                                        options={mappedExtraFilterByCategories[key]}
                                        handleChange={handleSelectChange}
                                    />
                                )}
                            </div>
                        );
                    }
                })}
            </div>
        );
    };

    return (
        <>
            <RenderColors />
            <RenderMultiSelect />
        </>
    );
};

export default GeneratedExtraFilters;
